import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import {
  TargetListsLineItemIdsListDocument,
  TargetListsLineItemIdsListQuery,
  TargetListsLineItemIdsListQueryVariables,
  TargetListsPlacementLineItemIdsListDocument,
  TargetListsPlacementLineItemIdsListQuery,
  TargetListsPlacementLineItemIdsListQueryVariables,
} from 'src/generated/client';
import { GetManyReferenceParams, SortPayload } from 'react-admin';
import { isNativeLineItemId } from '../../utility/pio-native-lineitem-fk-reads';

export const targetListsFilterByLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  includes: string,
) => {
  const queryResult = await apolloClient.query<
    TargetListsPlacementLineItemIdsListQuery,
    TargetListsPlacementLineItemIdsListQueryVariables
  >({
    query: TargetListsPlacementLineItemIdsListDocument,
    variables: {
      includes,
    },
  });
  const targetListsWithLineItemIdList =
    queryResult.data.targetLists?.nodes?.filter(node => node.placementLineItemIdsList?.length) ?? [];

  return {
    data: targetListsWithLineItemIdList,
    total: targetListsWithLineItemIdList.length,
  };
};

export const targetListsFilterByLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  lineItemId: string,
) => {
  try {
    return await targetListsFilterByNativeOrLegacyLineItemId(apolloClient, lineItemId);
  } catch (error) {
    console.error('targetListsFilterByLineItemId: native read failed, falling back to legacy', error);
    return targetListsFilterByLegacyLineItemId(apolloClient, lineItemId);
  }
};

type FmaCreativeNode = {
  id: number;
  name?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isApproved?: boolean | null;
  placementsLineItemId: string;
  lineItemId?: string | null;
};

type FmaCreativesQueryResult = {
  fmaCreatives?: {
    totalCount: number;
    nodes: FmaCreativeNode[];
  } | null;
};

type ClicksCreativeLineItemGridNode = {
  id: number;
  name?: string | null;
  title?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  targetingType?: string | null;
  targetListIdsList?: number[] | null;
  specialtyIdsList?: number[] | null;
  isApproved?: boolean | null;
  placementsLineItemId: string;
  lineItemId?: string | null;
};

type SponsoredContentTeaserLineItemGridNode = {
  id: number;
  title?: string | null;
  contentId?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  targetingType?: string | null;
  targetListIdsList?: number[] | null;
  specialtyIdsList?: number[] | null;
  isApproved?: boolean | null;
  placementsLineItemId: string;
  lineItemId?: string | null;
};

type LineItemLinkedCreativesQueryResult = {
  clicksCreatives?: {
    totalCount: number;
    nodes: ClicksCreativeLineItemGridNode[];
  } | null;
  sponsoredContentTeasers?: {
    totalCount: number;
    nodes: SponsoredContentTeaserLineItemGridNode[];
  } | null;
};

type NativeLineItemIdQueryResult = {
  lineItems?: {
    nodes: Array<{ id: string }>;
  } | null;
};

type LineItemExternalPioIdQueryResult = {
  lineItem?: {
    id: string;
    externalPioId?: string | null;
  } | null;
};

const FMA_CREATIVE_FIELDS = gql`
  fragment FmaCreativeLineItemGridFields on FmaCreative {
    id
    name
    startsAt
    endsAt
    isApproved
    placementsLineItemId
    lineItemId
  }
`;

const CLICKS_CREATIVE_LINE_ITEM_GRID_FIELDS = gql`
  fragment ClicksCreativeLineItemGridFields on ClicksCreative {
    id
    name
    title
    startsAt
    endsAt
    targetingType
    targetListIdsList
    specialtyIdsList
    isApproved
    placementsLineItemId
    lineItemId
  }
`;

const SPONSORED_CONTENT_TEASER_LINE_ITEM_GRID_FIELDS = gql`
  fragment SponsoredContentTeaserLineItemGridFields on SponsoredContentTeaser {
    id
    title
    contentId
    startsAt
    endsAt
    targetingType
    targetListIdsList
    specialtyIdsList
    isApproved
    placementsLineItemId
    lineItemId
  }
`;

export const FMA_CREATIVES_BY_LEGACY_LINE_ITEM_ID = gql`
  ${FMA_CREATIVE_FIELDS}
  query FmaCreativesByLegacyLineItemId(
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [FmaCreativesOrderBy!]
  ) {
    fmaCreatives(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: { placementsLineItemId: { equalTo: $legacyLineItemId } }
    ) {
      totalCount
      nodes {
        ...FmaCreativeLineItemGridFields
      }
    }
  }
`;

export const CLICKS_CREATIVES_BY_LEGACY_LINE_ITEM_ID = gql`
  ${CLICKS_CREATIVE_LINE_ITEM_GRID_FIELDS}
  query ClicksCreativesByLegacyLineItemId(
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [ClicksCreativesOrderBy!]
  ) {
    clicksCreatives(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: { placementsLineItemId: { equalTo: $legacyLineItemId } }
    ) {
      totalCount
      nodes {
        ...ClicksCreativeLineItemGridFields
      }
    }
  }
`;

export const SPONSORED_CONTENT_TEASERS_BY_LEGACY_LINE_ITEM_ID = gql`
  ${SPONSORED_CONTENT_TEASER_LINE_ITEM_GRID_FIELDS}
  query SponsoredContentTeasersByLegacyLineItemId(
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [SponsoredContentTeasersOrderBy!]
  ) {
    sponsoredContentTeasers(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: { placementsLineItemId: { equalTo: $legacyLineItemId } }
    ) {
      totalCount
      nodes {
        ...SponsoredContentTeaserLineItemGridFields
      }
    }
  }
`;

export const NATIVE_LINE_ITEM_ID_BY_EXTERNAL_PIO_ID = gql`
  query NativeLineItemIdByExternalPioId($externalPioId: String!) {
    lineItems(first: 1, filter: { externalPioId: { equalTo: $externalPioId } }) {
      nodes {
        id
      }
    }
  }
`;

export const LINE_ITEM_EXTERNAL_PIO_ID_BY_ID = gql`
  query LineItemExternalPioIdById($id: UUID!) {
    lineItem(id: $id) {
      id
      externalPioId
    }
  }
`;

export const FMA_CREATIVES_BY_NATIVE_OR_LEGACY_LINE_ITEM_ID = gql`
  ${FMA_CREATIVE_FIELDS}
  query FmaCreativesByNativeOrLegacyLineItemId(
    $nativeLineItemId: UUID!
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [FmaCreativesOrderBy!]
  ) {
    fmaCreatives(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: {
        or: [
          { lineItemId: { equalTo: $nativeLineItemId } }
          { and: [{ lineItemId: { isNull: true } }, { placementsLineItemId: { equalTo: $legacyLineItemId } }] }
        ]
      }
    ) {
      totalCount
      nodes {
        ...FmaCreativeLineItemGridFields
      }
    }
  }
`;

export const CLICKS_CREATIVES_BY_NATIVE_OR_LEGACY_LINE_ITEM_ID = gql`
  ${CLICKS_CREATIVE_LINE_ITEM_GRID_FIELDS}
  query ClicksCreativesByNativeOrLegacyLineItemId(
    $nativeLineItemId: UUID!
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [ClicksCreativesOrderBy!]
  ) {
    clicksCreatives(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: {
        or: [
          { lineItemId: { equalTo: $nativeLineItemId } }
          { and: [{ lineItemId: { isNull: true } }, { placementsLineItemId: { equalTo: $legacyLineItemId } }] }
        ]
      }
    ) {
      totalCount
      nodes {
        ...ClicksCreativeLineItemGridFields
      }
    }
  }
`;

export const SPONSORED_CONTENT_TEASERS_BY_NATIVE_OR_LEGACY_LINE_ITEM_ID = gql`
  ${SPONSORED_CONTENT_TEASER_LINE_ITEM_GRID_FIELDS}
  query SponsoredContentTeasersByNativeOrLegacyLineItemId(
    $nativeLineItemId: UUID!
    $legacyLineItemId: String!
    $first: Int
    $offset: Int
    $orderBy: [SponsoredContentTeasersOrderBy!]
  ) {
    sponsoredContentTeasers(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: {
        or: [
          { lineItemId: { equalTo: $nativeLineItemId } }
          { and: [{ lineItemId: { isNull: true } }, { placementsLineItemId: { equalTo: $legacyLineItemId } }] }
        ]
      }
    ) {
      totalCount
      nodes {
        ...SponsoredContentTeaserLineItemGridFields
      }
    }
  }
`;

const toFmaCreativesOrderBy = (sort?: SortPayload): string[] | undefined => {
  if (!sort?.field || !sort.order) return undefined;

  const field = sort.field.replace(/([A-Z])/g, '_$1').toUpperCase();
  const order = sort.order.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';

  return [`${field}_${order}`];
};

const getPaginationVariables = (params: GetManyReferenceParams) => {
  const page = params.pagination?.page ?? 1;
  const perPage = params.pagination?.perPage;

  return {
    first: perPage,
    offset: perPage ? (page - 1) * perPage : undefined,
  };
};

const toGetManyReferenceResult = (queryResult: FmaCreativesQueryResult) => ({
  data: queryResult.fmaCreatives?.nodes ?? [],
  total: queryResult.fmaCreatives?.totalCount ?? 0,
});

const lineItemLinkedCreativeResources = {
  clicksCreatives: {
    legacyQuery: CLICKS_CREATIVES_BY_LEGACY_LINE_ITEM_ID,
    nativeOrLegacyQuery: CLICKS_CREATIVES_BY_NATIVE_OR_LEGACY_LINE_ITEM_ID,
    resultKey: 'clicksCreatives',
    errorLabel: 'clicksCreativesFilterByNativeOrLegacyLineItemId',
  },
  sponsoredContentTeasers: {
    legacyQuery: SPONSORED_CONTENT_TEASERS_BY_LEGACY_LINE_ITEM_ID,
    nativeOrLegacyQuery: SPONSORED_CONTENT_TEASERS_BY_NATIVE_OR_LEGACY_LINE_ITEM_ID,
    resultKey: 'sponsoredContentTeasers',
    errorLabel: 'sponsoredContentTeasersFilterByNativeOrLegacyLineItemId',
  },
} as const;

export type LineItemLinkedCreativeResource = keyof typeof lineItemLinkedCreativeResources;

const toLineItemLinkedCreativesResult = (
  queryResult: LineItemLinkedCreativesQueryResult,
  resource: LineItemLinkedCreativeResource,
) => {
  const resultKey = lineItemLinkedCreativeResources[resource].resultKey;
  const connection = queryResult[resultKey];

  return {
    data: connection?.nodes ?? [],
    total: connection?.totalCount ?? 0,
  };
};

export const fmaCreativesFilterByLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  params: GetManyReferenceParams,
) => {
  const queryResult = await apolloClient.query<FmaCreativesQueryResult>({
    query: FMA_CREATIVES_BY_LEGACY_LINE_ITEM_ID,
    variables: {
      legacyLineItemId: String(params.id),
      ...getPaginationVariables(params),
      orderBy: toFmaCreativesOrderBy(params.sort),
    },
  });

  return toGetManyReferenceResult(queryResult.data);
};

export const lineItemLinkedCreativesFilterByLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  resource: LineItemLinkedCreativeResource,
  params: GetManyReferenceParams,
) => {
  const config = lineItemLinkedCreativeResources[resource];
  const queryResult = await apolloClient.query<LineItemLinkedCreativesQueryResult>({
    query: config.legacyQuery,
    variables: {
      legacyLineItemId: String(params.id),
      ...getPaginationVariables(params),
      orderBy: toFmaCreativesOrderBy(params.sort),
    },
  });

  return toLineItemLinkedCreativesResult(queryResult.data, resource);
};

export const getNativeLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  legacyLineItemId: string,
): Promise<string | undefined> => {
  // MD-5145: a native line item's id IS its native UUID. The incoming read id is
  // a native UUID for natively-built line items (no externalPioId to translate),
  // so use it directly and skip the externalPioId lookup. Mirrors
  // getTargetListLookupLineItemIds.
  if (isNativeLineItemId(legacyLineItemId)) return legacyLineItemId;

  const queryResult = await apolloClient.query<NativeLineItemIdQueryResult>({
    query: NATIVE_LINE_ITEM_ID_BY_EXTERNAL_PIO_ID,
    variables: {
      externalPioId: legacyLineItemId,
    },
  });

  return queryResult.data.lineItems?.nodes[0]?.id;
};

const getLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  nativeLineItemId: string,
): Promise<string | undefined> => {
  const queryResult = await apolloClient.query<LineItemExternalPioIdQueryResult>({
    query: LINE_ITEM_EXTERNAL_PIO_ID_BY_ID,
    variables: {
      id: nativeLineItemId,
    },
  });

  return queryResult.data.lineItem?.externalPioId ?? undefined;
};

const getTargetListLookupLineItemIds = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  lineItemId: string,
) => {
  if (isNativeLineItemId(lineItemId)) {
    const legacyLineItemId = await getLegacyLineItemId(apolloClient, lineItemId);

    return legacyLineItemId ? [lineItemId, legacyLineItemId] : [lineItemId];
  }

  const nativeLineItemId = await getNativeLineItemId(apolloClient, lineItemId);

  return nativeLineItemId ? [nativeLineItemId, lineItemId] : [lineItemId];
};

export const targetListsFilterByNativeOrLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  lineItemId: string,
) => {
  const lineItemIds = await getTargetListLookupLineItemIds(apolloClient, lineItemId);
  const queryResult = await apolloClient.query<
    TargetListsLineItemIdsListQuery,
    TargetListsLineItemIdsListQueryVariables
  >({
    query: TargetListsLineItemIdsListDocument,
    variables: {
      lineItemIds,
    },
  });
  const targetListsWithLineItemIdList =
    queryResult.data.targetLists?.nodes?.filter(node => node.lineItemIdsList?.length) ?? [];

  return {
    data: targetListsWithLineItemIdList,
    total: targetListsWithLineItemIdList.length,
  };
};

export const lineItemLinkedCreativesFilterByNativeOrLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  resource: LineItemLinkedCreativeResource,
  params: GetManyReferenceParams,
) => {
  const config = lineItemLinkedCreativeResources[resource];
  const legacyLineItemId = String(params.id);

  try {
    const nativeLineItemId = await getNativeLineItemId(apolloClient, legacyLineItemId);

    if (!nativeLineItemId) {
      return lineItemLinkedCreativesFilterByLegacyLineItemId(apolloClient, resource, params);
    }

    const queryResult = await apolloClient.query<LineItemLinkedCreativesQueryResult>({
      query: config.nativeOrLegacyQuery,
      variables: {
        nativeLineItemId,
        legacyLineItemId,
        ...getPaginationVariables(params),
        orderBy: toFmaCreativesOrderBy(params.sort),
      },
    });

    return toLineItemLinkedCreativesResult(queryResult.data, resource);
  } catch (error) {
    console.error(`${config.errorLabel}: native read failed, falling back to legacy`, error);
    return lineItemLinkedCreativesFilterByLegacyLineItemId(apolloClient, resource, params);
  }
};

export const fmaCreativesFilterByNativeOrLegacyLineItemId = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  params: GetManyReferenceParams,
) => {
  const legacyLineItemId = String(params.id);

  try {
    const nativeLineItemId = await getNativeLineItemId(apolloClient, legacyLineItemId);

    if (!nativeLineItemId) {
      return fmaCreativesFilterByLegacyLineItemId(apolloClient, params);
    }

    const queryResult = await apolloClient.query<FmaCreativesQueryResult>({
      query: FMA_CREATIVES_BY_NATIVE_OR_LEGACY_LINE_ITEM_ID,
      variables: {
        nativeLineItemId,
        legacyLineItemId,
        ...getPaginationVariables(params),
        orderBy: toFmaCreativesOrderBy(params.sort),
      },
    });

    return toGetManyReferenceResult(queryResult.data);
  } catch (error) {
    console.error('fmaCreativesFilterByNativeOrLegacyLineItemId: native read failed, falling back to legacy', error);
    return fmaCreativesFilterByLegacyLineItemId(apolloClient, params);
  }
};
